import LoadingSpinner from "@/components/LoadingSpinner";
import PollCardWrapper from "@/components/Polls/PollCardWrapper";
import { Tabs } from "@/components/Tabs";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { authClient } from "@/lib/better-auth/client";
import MainLayout from "@/lib/layout/MainLayout";
import { usePolls } from "@/lib/tanstack-query/hooks/usePolls";
import { cn } from "@/lib/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2, PlusCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";

export const Route = createFileRoute("/_dashboard/")({
  component: Home,
});

function Home() {
  const {
    data: userSession,
    isRefetching: isRefetchingSession,
    isPending: isPendingSession,
  } = authClient.useSession();
  const [sort, setSort] = useState<"best" | "new">("best");
  const [nsfw, setNsfw] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"for-you" | "following">(
    "for-you"
  );
  const { ref, inView } = useInView();

  let {
    data: polls,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = usePolls({ sort, onlyFollowing: activeTab === "following", nsfw });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, isLoading]);

  const flatPolls = useMemo(
    () => polls?.pages.flatMap((page) => page) ?? [],
    [polls]
  );

  return (
    <>
      <MainLayout.Header
        additionalClassName={cn(userSession?.user ? "border-b-0" : null)}
        headline="Would you rather questions"
      >
        <div className="flex items-center justify-between gap-4 dark:text-white">
          <div className="flex gap-2">
            <Select
              value={sort}
              onValueChange={(value) => setSort(value as "best" | "new")}
            >
              <SelectTrigger className="dark:bg-button-background-wu bg-button-background-wu hover:bg-button-hover-background-wu dark:hover:bg-button-hover-background-wu cursor-pointer rounded-full border-none">
                <SelectValue placeholder="Select a piece type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="best">Best</SelectItem>
                <SelectItem value="new">New</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => setNsfw(!nsfw)}
              className={cn(
                "rounded-full transition-none",
                nsfw
                  ? "bg-primary-wu dark:bg-primary-wu hover:bg-primary-wu dark:hover:bg-primary-wu hover:brightness-90"
                  : "dark:bg-button-background-wu bg-button-background-wu hover:bg-button-hover-background-wu dark:hover:bg-button-hover-background-wu"
              )}
              variant={"ghost"}
            >
              NSFW
            </Button>
          </div>
          <Link
            to={"/create-poll"}
            className="bg-primary-wu flex gap-2 font-semibold rounded-full px-4 py-2 text-white transition-colors hover:brightness-90"
          >
            <PlusCircle className={cn("size-5")} />
            Create Poll
          </Link>
        </div>
      </MainLayout.Header>
      {userSession?.user ? (
        <MainLayout.Sticky>
          <Tabs
            tabs={[
              { value: "for-you", label: "For you" },
              { value: "following", label: "Following" },
            ]}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </MainLayout.Sticky>
      ) : null}

      <MainLayout.Content hasPadding={false}>
        {flatPolls.length > 0 && (
          <PollCardWrapper key={activeTab} polls={flatPolls} />
        )}
        <div>
          {isLoading ? <LoadingSpinner /> : null}
          {hasNextPage ? <LoadingSpinner ref={ref} /> : null}
        </div>
      </MainLayout.Content>
    </>
  );
}
